import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Stack, Check, BellSimple, BellSimpleSlash, BookOpenText, Books } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

export default function SeriesPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/series/${slug}`);
      setData(data);
      setFollowing(data.is_following);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  async function toggleFollow() {
    if (!user) { toast.error("Entra para seguir séries"); navigate("/login"); return; }
    setBusy(true);
    try {
      if (following) { await api.delete(`/me/series-follow/${data.series_id}`); setFollowing(false); toast.success("Deixaste de seguir"); }
      else { await api.post(`/me/series-follow/${data.series_id}`); setFollowing(true); toast.success("A seguir! Serás avisado de novos volumes."); }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (!data) return <div className="pt-32 text-center text-zinc-500">Série não encontrada.</div>;

  const pct = data.volume_count ? Math.round((data.owned_count / data.volume_count) * 100) : 0;

  return (
    <div className="pb-24 relative" data-testid="series-page">
      <Seo title={data.name} description={data.description} image={data.image_url} path={`/serie/${slug}`} type="book" />
      <div className="absolute top-0 left-0 right-0 h-[360px] overflow-hidden -z-10">
        {data.image_url && <img src={resolveImageUrl(data.image_url)} alt="" className="w-full h-full object-cover opacity-20 blur-2xl scale-110" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/40 via-[#09090B]/85 to-[#09090B]" />
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-24">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="w-40 aspect-[2/3] rounded-2xl overflow-hidden border border-white/10 bg-[#121217] flex-shrink-0 shadow-2xl">
            {data.image_url ? <img src={resolveImageUrl(data.image_url)} alt={data.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><Books size={30} /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-accent text-xs text-[#E63946] mb-2 flex items-center gap-1.5"><Stack size={14} weight="bold" /> SÉRIE</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">{data.name}</h1>
            {data.description && <p className="text-zinc-300 mt-3 max-w-2xl leading-relaxed">{data.description}</p>}
            <div className="mt-4 flex items-center gap-4 flex-wrap">
              <span className="text-sm text-zinc-400"><span className="font-display font-bold text-white">{data.volume_count}</span> volumes</span>
              <span className="text-sm text-zinc-400"><span className="font-display font-bold text-white">{data.follower_count}</span> a seguir</span>
              <Button onClick={toggleFollow} disabled={busy} className={following ? "bg-white/10 hover:bg-white/15 text-white" : "btn-crimson"} data-testid="follow-series-btn">
                {following ? <><BellSimpleSlash size={16} weight="bold" className="mr-1.5" /> A seguir</> : <><BellSimple size={16} weight="bold" className="mr-1.5" /> Seguir série</>}
              </Button>
            </div>
            {user && data.volume_count > 0 && (
              <div className="mt-4 max-w-md">
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-zinc-400">A tua coleção</span><span className="font-mono-accent text-zinc-500">{data.owned_count}/{data.volume_count} · {pct}%</span></div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#E63946,#7209B7)" }} /></div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10">
          <div className="font-mono-accent text-xs text-zinc-500 mb-4 flex items-center gap-2"><BookOpenText size={16} weight="bold" className="text-[#E63946]" /> VOLUMES ({data.volume_count})</div>
          {data.volumes.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-2xl p-12 text-center text-sm text-zinc-500">Ainda sem volumes associados a esta série.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-4">
              {data.volumes.map((v) => (
                <Link key={v.manga_id} to={`/manga/${v.slug || v.manga_id}`} className="group" data-testid={`series-page-vol-${v.manga_id}`} title={v.title}>
                  <div className={`aspect-[2/3] overflow-hidden rounded-lg border ${v.owned ? "border-emerald-500/50" : "border-white/5"} bg-[#121217] relative`}>
                    {v.cover_url ? <img src={v.cover_url} alt={v.title} className={`w-full h-full object-cover ${v.owned ? "" : "opacity-50 group-hover:opacity-80"}`} /> : <div className="w-full h-full grid place-items-center text-zinc-700 text-[10px]">{v.volume ? `#${v.volume}` : "—"}</div>}
                    {v.owned && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 grid place-items-center"><Check size={12} weight="bold" className="text-white" /></div>}
                  </div>
                  {v.volume != null && <div className="mt-1 text-center font-mono-accent text-[10px] text-zinc-500">Vol. {v.volume}</div>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
